import React from 'react'

import { Box, Button } from '@factorialco/deprecated-design-system'
import {
  AccountPageDocument,
  AddBankAccountMembershipMemberDocument,
  BankAccountMembershipMembersDocument,
} from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { BankAccountMembershipSelector } from 'modules/banking/components/BankAccountMembershipSelector'
import { EmployeeSelectorV2MultiSelectState } from 'modules/employees/components/EmployeeSelectorV2/types'

interface AddBankAccountMembershipModalProps {
  onClose: () => void
  bankAccountId: string
}

const AddBankAccountMembershipModal = ({
  onClose,
  bankAccountId,
}: AddBankAccountMembershipModalProps) => {
  const { company } = useCurrentSession()
  const [employeeSelection, setEmployeeSelection] =
    React.useState<EmployeeSelectorV2MultiSelectState>({
      type: 'manual',
      ids: [],
    })

  const [addMembership] = useMutation(AddBankAccountMembershipMemberDocument, {
    refetchQueries: [BankAccountMembershipMembersDocument, AccountPageDocument],
    extractorFn: (data) => data.bankingMutations.upsertBankingBankAccountMembership,
  })

  const handleSubmit = async () => {
    const hasEmployees =
      (employeeSelection.type === 'manual' && employeeSelection.ids.length > 0) ||
      employeeSelection.type === 'all'

    if (!hasEmployees) return

    track(trackingEvents.accounting.banking.addPeopleBankAccount)
    const result = await addMembership({
      variables: {
        companyId: company.id,
        bankAccountId: bankAccountId,
        employeeSelection: employeeSelection,
      },
    })

    const handled = handleResult(result, () => undefined)

    if (handled.success) {
      onClose()
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
        time: 2000,
      })
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('errors.generic_error'),
      })
    }
  }

  const isDisabled =
    employeeSelection.type === 'manual' ? employeeSelection.ids.length === 0 : false

  return (
    <Modal
      title={i18n.t('banking.accounts.add_membership_modal.title')}
      description={i18n.t('banking.accounts.add_membership_modal.description')}
      illustration={'add-user'}
      onClose={onClose}
      footer={
        <Box flexDirection="row" gap="s8">
          <Button type="secondary" onClick={onClose}>
            {i18n.t('common.cancel')}
          </Button>
          <Button onClick={handleSubmit} disabled={isDisabled}>
            {i18n.t('common.add')}
          </Button>
        </Box>
      }
    >
      <Box flexDirection="column" gap="s24">
        <BankAccountMembershipSelector onChange={setEmployeeSelection} />
      </Box>
    </Modal>
  )
}

export default AddBankAccountMembershipModal
